import React, { useEffect, useState, useCallback, useRef } from "react";
import { Col, Row } from "reactstrap";
import ApiService from "../../services/apiService";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import { toast } from "react-toastify";
import { LucideRefreshCcw } from "lucide-react";
import pumpon from "../IrootImages/PUMPON.png";
import pumpoff from "../IrootImages/PUMPOFF.png";

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY       = "#1e563d";
const PRIMARY_LIGHT = "rgba(30, 86, 61, 0.16)";
const PUMP_BLUE     = "#185FA5";
const PUMP_BLUE_BG  = "#E6F1FB";
const DANGER        = "#DC3545";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PumpData {
  id:                   number;
  deviceId:             number;
  deviceName:           string;
  deviceType:           string;
  instrumentName:       string;
  instrumentNumber:     number | null;
  dateTime:             string | null;
  packetType:           string | null;
  classType:            string | null;
  firmwareVersion:      number | null;
  batteryVoltage:       number | null;
  temperature:          number | null;
  status:               number | null;
  highTemperatureAlarm: number | null;
  lowBatteryAlarm:      number | null;
  alarmBit:             number | null;
  auto_Manual:          number | null;
  operateBit:           number | null;
  dI1Counter:           number | null;
  doorStatus:           number | null;
  // Pump specific
  p_SysHealthy:         number | null;
  p_CurrentFault:       number | null;
  p_PhaseFault:         number | null;
  p_Phase1:             number | null;
  p_Phase2:             number | null;
  p_Phase3:             number | null;
  p_OverCurrentBit:     number | null;
  p_UnderCurrentBit:    number | null;
  p_PowerStatus:        number | null;
  ctValue:              number | null;
  ackBit:               number | null;
  p_LocalRemote:        number | null;
  autoSwitch    :           number | null;
  // Config
  type:                 string | null;
  phase:                number | null;
  underCurrent:         number | null;
  overCurrent:          number | null;
  capacity:             number | null;
  byPassPhase:          number | null;
  byPassUnderCurrent:   number | null;
  byPassOverCurrent:    number | null;
  // Signal
  rssi:                 number | null;
  snr:                  number | null;
  spreadingFactor:      number | null;
}

export interface TimelineEvent {
  id:              number | string;
  instrumentId:    number | string;
  eventType:       string;        // mapped key used for config lookup
  operationType:   string;        // raw label from API e.g. "Pump On", "Phase Fault"
  dateTime:        string;
  alarmEvent:      number;        // 0 = Event, 1 = Alarm
  description?:    string | null;
  userName?:       string | null;
  autoManual?:     string | null; // "Auto Mode" | "Manual Mode" | null
  temperature?:    number | null;
  batteryVoltage?: number | null;
  rssi?: number | null;
  instrumentName?: string | null;
}

interface PumpDetailProps {
  id:            number | string;
  historyTable?: React.ReactNode;
  onCommand?:    (cmd: "start" | "stop") => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function safeNum(v: number | null | undefined, s = ""): string {
  if (v == null || isNaN(v as number)) return "—";
  return `${v}${s}`;
}
function safeStr(v: string | null | undefined): string {
  return !v || v.trim() === "" ? "—" : v;
}
function fmt(dt: string | null | undefined, mode: "full" | "time" | "date" = "full"): string {
  if (!dt) return "—";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "—";
  if (mode === "time") return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  if (mode === "date") return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}
function bitLabel(v: number | null | undefined, on: string, off: string): string {
  return v === 1 ? on : v === 0 ? off : "—";
}
function groupByDate(evs: TimelineEvent[]) {
  const m = new Map<string, TimelineEvent[]>();
  evs.forEach(ev => {
    const k = fmt(ev.dateTime, "date");
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(ev);
  });
  return Array.from(m.entries()).map(([date, items]) => ({ date, items }));
}

// ─── Timeline event config ────────────────────────────────────────────────────

/** Maps raw API operationType → visual config */
function evCfg(operationType: string, alarmEvent: number): {
  dot: string; bg: string; border: string; category: string;
} {
  const op = operationType.toLowerCase().trim();

  if (alarmEvent === 1) {
    // All alarm events — red
    if (op.includes("phase"))       return { dot: "#A32D2D", bg: "#fef2f2", border: "#fca5a5", category: "alarm" };
    if (op.includes("over"))        return { dot: "#854F0B", bg: "#faeeda", border: "#fde68a", category: "alarm" };
    if (op.includes("under"))       return { dot: "#854F0B", bg: "#faeeda", border: "#fde68a", category: "alarm" };
    if (op.includes("temp"))        return { dot: "#854F0B", bg: "#faeeda", border: "#fde68a", category: "alarm" };
    if (op.includes("battery"))     return { dot: "#A32D2D", bg: "#fef2f2", border: "#fca5a5", category: "alarm" };
    return                                 { dot: "#A32D2D", bg: "#fef2f2", border: "#fca5a5", category: "alarm" };
  }

  // Events (alarmEvent === 0)
  if (op.includes("pump on"))       return { dot: PRIMARY,   bg: "#e8f2ed", border: "#c6ddd3", category: "pump" };
  if (op.includes("pump off"))      return { dot: "#475569", bg: "#f1f5f9", border: "#e2e8f0", category: "pump" };
  if (op.includes("phase ok"))      return { dot: "#3B6D11", bg: "#e8f2ed", border: "#c6ddd3", category: "alarm" };
  if (op.includes("auto"))          return { dot: PUMP_BLUE, bg: PUMP_BLUE_BG, border: "#bfdbfe", category: "mode" };
  if (op.includes("manual"))        return { dot: "#854F0B", bg: "#faeeda", border: "#fde68a", category: "mode" };
  if (op.includes("remote"))        return { dot: "#0F6E56", bg: "#E1F5EE", border: "#9FE1CB", category: "mode" };
  if (op.includes("local"))         return { dot: "#64748b", bg: "#f1f5f9", border: "#e2e8f0", category: "mode" };
  if (op.includes("command"))       return { dot: PUMP_BLUE, bg: PUMP_BLUE_BG, border: "#bfdbfe", category: "command" };
  return                                   { dot: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0", category: "other" };
}

const TL_FILTERS = [
  { key: "all",     label: "All",      match: (_ev: TimelineEvent) => true },
  { key: "pump",    label: "Pump",     match: (ev: TimelineEvent) => evCfg(ev.operationType, ev.alarmEvent).category === "pump" },
  { key: "alarm",   label: "Alarms",   match: (ev: TimelineEvent) => ev.alarmEvent === 1 || evCfg(ev.operationType, ev.alarmEvent).category === "alarm" },
  { key: "mode",    label: "Mode",     match: (ev: TimelineEvent) => evCfg(ev.operationType, ev.alarmEvent).category === "mode" },
  { key: "command", label: "Commands", match: (ev: TimelineEvent) => evCfg(ev.operationType, ev.alarmEvent).category === "command" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const DataRow: React.FC<{
  label:      string;
  value:      React.ReactNode;
  valueColor?: string;
  alarm?:     boolean;
  last?:      boolean;
}> = ({ label, value, valueColor, alarm, last }) => (
  <tr className="row-values">
    <td className="td-label">{label}</td>
    <td className="td-value">
      {alarm && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#E24B4A", marginRight: 5, verticalAlign: "middle" }} />}
      {value}
    </td>
  </tr>
);

const SectionHead: React.FC<{ title: string }> = ({ title }) => (
  <div className="section-head">{title}</div>
);

const Sig: React.FC<{ rssi: number | null | undefined }> = ({ rssi }) => {
  const v = rssi != null && !isNaN(rssi as number);
  const s = !v ? 0 : rssi! >= -70 ? 4 : rssi! >= -80 ? 3 : rssi! >= -90 ? 2 : 1;
  const c = s === 4 ? PRIMARY : s === 3 ? "#3B6D11" : s === 2 ? "#854F0B" : "#E24B4A";
  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2 }}>
      {[4, 7, 10, 13].map((h, i) => (
        <span key={i} style={{ display: "inline-block", width: 3, height: h, borderRadius: 1, background: i < s ? c : "#e2e8f0" }} />
      ))}
      <span style={{ fontSize: 12, color: v ? c : "#94a3b8", marginLeft: 4 }}>{v ? `${rssi} dBm` : "—"}</span>
    </span>
  );
};

/** Phase indicator pills */
const PhaseRow: React.FC<{ phase1: number | null; phase2: number | null; phase3: number | null }> = ({
  phase1, phase2, phase3,
}) => {
  const phases = [
    { label: "R", val: phase1 },
    { label: "Y", val: phase2 },
    { label: "B", val: phase3 },
  ];
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {phases.map(({ label, val }) => {
        const fault = val === 1;
        return (
          <span key={label} style={{
            padding: "1px 7px", borderRadius: 5, fontSize: 11, fontWeight: 600,
            background: fault ? "#fef2f2" : "#e8f2ed",
            color:      fault ? "#A32D2D" : "#3B6D11",
            border:     `1px solid ${fault ? "#fca5a5" : "#c6ddd3"}`,
          }}>
            {label}: {val === null ? "—" : fault ? "Fault" : "OK"}
          </span>
        );
      })}
    </span>
  );
};

/** Auto/Manual slider toggle */
const AutoManualToggle: React.FC<{
  isAuto:   boolean;
  busy:     boolean;
  onToggle: () => void;
}> = ({ isAuto, busy, onToggle }) => (
  <div style={styles.sliderWrap}>
    <span style={{ ...styles.sliderSideLabel, color: !isAuto ? "#854F0B" : "#94a3b8", fontWeight: !isAuto ? 600 : 400 }}>
      Manual
    </span>
    <button
      onClick={onToggle}
      disabled={busy}
      role="switch"
      aria-checked={isAuto}
      title={isAuto ? "Switch to Manual" : "Switch to Auto"}
      style={{
        ...styles.sliderTrack,
        background:  isAuto ? PUMP_BLUE : "#854F0B",
        opacity:     busy ? 0.55 : 1,
        cursor:      busy ? "not-allowed" : "pointer",
      }}
    >
      <span style={{
        ...styles.sliderThumb,
        transform: isAuto ? "translateX(20px)" : "translateX(0px)",
      }}>
        {busy && (
          <i className="ti ti-loader-2" aria-hidden="true"
             style={{ fontSize: 8, color: isAuto ? PUMP_BLUE : "#854F0B", animation: "vdspin .9s linear infinite" }} />
        )}
      </span>
    </button>
    <span style={{ ...styles.sliderSideLabel, color: isAuto ? PUMP_BLUE : "#94a3b8", fontWeight: isAuto ? 600 : 400 }}>
      Auto
    </span>
  </div>
);

/** Timeline date divider */
const DateDiv: React.FC<{ date: string }> = ({ date }) => {
  const today = fmt(new Date().toISOString(), "date");
  const yest  = fmt(new Date(Date.now() - 86_400_000).toISOString(), "date");
  const label = date === today ? "Today" : date === yest ? "Yesterday" : date;
  return (
    <div style={styles.dateDivider}>
      <div style={styles.dateDivLine} />
      <span style={styles.dateDivLabel}>{label}</span>
      <div style={styles.dateDivLine} />
    </div>
  );
};

/** Timeline event card — circle shows "A" (alarm) or "E" (event) */
const TLCard: React.FC<{ ev: TimelineEvent; isLast: boolean }> = ({ ev, isLast }) => {
  const cfg      = evCfg(ev.operationType, ev.alarmEvent);
  const isAlarm  = ev.alarmEvent === 1;
  const letter   = isAlarm ? "A" : "E";

  return (
    <div  className="tlRow">
      {/* Spine */}
      <div  className="tlSpine">
        <div className="tlDot" style={{

          background: cfg.bg,
          border: `1.5px solid ${cfg.dot}`,
        }}>
          <span style={{
            fontSize:   15,
            fontWeight: 800,
            color:      cfg.dot,
            lineHeight: 1,
            fontFamily: "monospace",
          }}>
            {letter}
          </span>
        </div>
        {!isLast && <div  className="tlLine"/>}
      </div>

      {/* Card */}
      <div className="tlCard" style={{  marginBottom: isLast ? 0 : 6 }}>
        {/* Head row: label + time */}
        <div  className="tlCardHead">
          <span className="tlTag" style={{

            background: cfg.bg,
            color:      cfg.dot,
            border:     `1px solid ${cfg.border}`,
          }}>
            {ev.operationType}
          </span>
          <span className="tlTime" >{fmt(ev.dateTime, "time")}</span>
        </div>

        {/* Chips row: userName + autoManual */}
        {(ev.userName || ev.autoManual || ev.instrumentName) && (
          <div style={styles.tlChips}>
            {ev.userName && (
              <span className="tlChip" style={{  background: "#f0f4ff", color: "#3b5bdb", border: "1px solid #c5d0e6" }}>

                {ev.userName}
              </span>
            )}
            {ev.autoManual && (
              <span className="tlChip" style={{

                background: ev.autoManual === "Auto Mode" ? PUMP_BLUE_BG : "#faeeda",
                color:      ev.autoManual === "Auto Mode" ? PUMP_BLUE    : "#854F0B",
                border:     `1px solid ${ev.autoManual === "Auto Mode" ? "#bfdbfe" : "#fde68a"}`,
              }}>

                {ev.autoManual}
              </span>
            )}

            {ev.instrumentName && (   // ✅ add this chip
                  <span className="tlChip" style={{ background: "#e8f2ed", color: PRIMARY, border: "1px solid #c6ddd3" }}>
                    {ev.instrumentName}
                  </span>
                )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── PumpDetail ───────────────────────────────────────────────────────────────

const PumpDetail: React.FC<PumpDetailProps> = ({ id, historyTable, onCommand }) => {
  const [data,         setData]         = useState<PumpData | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [cmdPending,   setCmdPending]   = useState<"start" | "stop" | null>(null);
  const [modeBusy,     setModeBusy]     = useState(false);
  const [rotating,     setRotating]     = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const [tlEvs,        setTlEvs]        = useState<TimelineEvent[]>([]);
  const [tlLoading,    setTlLoading]    = useState(true);
  const [tlLoadingMore,setTlLoadingMore]= useState(false);  // loading next page
  const [tlError,      setTlError]      = useState<string | null>(null);
  const [tlFilter,     setTlFilter]     = useState("all");
  const [tlRef,        setTlRef]        = useState(false);
  const [tlHasMore,    setTlHasMore]    = useState(true);   // server says more pages?
  const tlPageRef   = useRef(1);                            // current page (ref to avoid stale closure)
  const tlScrollRef = useRef<HTMLDivElement>(null);
  const tlSentinel  = useRef<HTMLDivElement>(null);
  const tlFetching  = useRef(false);                        // guard: prevent double-fetch
  const tlHasMoreRef = useRef(true);
  const PAGE_SIZE = 10;

  // ── Timeline loader ───────────────────────────────────────────────────────

  /**
   * loadTL(page, append)
   *   page   — which page to fetch (1 = first)
   *   append — if true, push onto existing list; if false, replace list (reset)
   */
  const setHasMore = (v: boolean) => {
    tlHasMoreRef.current = v;
    setTlHasMore(v);
  };

  const loadTL = useCallback(async (page: number, append: boolean) => {
    if (!data?.id) return;
    if (tlFetching.current) return;          // already in flight
    tlFetching.current = true;

    if (page === 1) {
      setTlLoading(true);
      setTlError(null);
      setTlEvs([]);
    } else {
      setTlLoadingMore(true);
    }
    const now = new Date();

const ist = new Date(
  now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
);

const isoIST =
  `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}-${String(ist.getDate()).padStart(2, "0")}T` +
  `${String(ist.getHours()).padStart(2, "0")}:${String(ist.getMinutes()).padStart(2, "0")}:${String(ist.getSeconds()).padStart(2, "0")}`;



    try {
      const res = await ApiService.axiosApi(`/timelinemaster/get-timeline`, {
        method: "GET",
        params: {
          instrumentId: data.id,
          dateTime:    isoIST,
          pageNumber:   page,
          pageSize:     PAGE_SIZE,
        },
      });

      if (res.success) {
        const raw: any[] = Array.isArray(res.data.data) ? res.data.data : [];

        const mapped: TimelineEvent[] = raw.map((item: any) => ({
          id:            item.id,
          instrumentId:  item.instrumentId ?? "",
          dateTime:      item.dateTime,
          alarmEvent:    item.alarm_Event ?? 0,
          operationType: item.operationType ?? "Unknown",
          eventType:     item.operationType?.toLowerCase().replace(/\s+/g, "_") ?? "unknown",
          userName:      item.userName ?? null,
          autoManual:    item.auto_Manual ?? null,
          description: item.remark ?? null,
          instrumentName: item.instrumentName ?? null,   // ✅ add this

        }));

        // If server returns fewer than PAGE_SIZE, no more pages
        setHasMore(raw.length === PAGE_SIZE);

        tlPageRef.current = page;

        setTlEvs(prev => append ? [...prev, ...mapped] : mapped);
      } else {
        setTlError("Failed to load timeline.");
      }
    } catch {
      setTlError("Network error.");
    } finally {
      setTlLoading(false);
      setTlLoadingMore(false);
      setTlRef(false);
      tlFetching.current = false;
    }
  }, [data?.id]);

  // ── Refresh helper (called from refresh button / after commands) ──────────
  const refreshTL = useCallback(async () => {
    setTlRef(true);
    tlPageRef.current = 1;
    setHasMore(true);
    await loadTL(1, false);
  }, [loadTL]);

  // ── Data loaders ──────────────────────────────────────────────────────────

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await ApiService.axiosApi(`/instrumentmaster/${id}`, { method: "GET" });
      if (res.success) setData(res.data.data ?? res.data);
      else setError("Failed to load pump data.");
    } catch { setError("Network error."); }
    finally { setLoading(false); setRefreshing(false); }
  }, [id]);

  useEffect(() => { if (id) { loadData(); } }, [id, loadData]);

  // Load first page once data.id is known
  useEffect(() => {
    if (data?.id) {
      tlPageRef.current = 1;
      setHasMore(true);
      loadTL(1, false);
    }
  }, [data?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Infinite scroll — fires API for next page when sentinel visible ────────
  useEffect(() => {
    const root = tlScrollRef.current;
    const target = tlSentinel.current;

    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (
          entry.isIntersecting &&
          tlHasMoreRef.current &&
          !tlFetching.current
        ) {
          loadTL(tlPageRef.current + 1, true);
        }
      },
      {
        root,
        threshold: 0,
        rootMargin: "300px",
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [tlEvs.length]);

  // ── Start / Stop command ──────────────────────────────────────────────────

  const handleCmd = async (cmd: "start" | "stop") => {
    setCmdPending(cmd);
    setRotating(true);
    try {
      const res = await ApiService.axiosApi("/pumpcommand/pump-start/stop", {
        method: "POST",
        data: { instrumentId: data?.id, onOff: cmd === "start" ? 1 : 0 },
      });
      if (res.success && res.data?.success) {
        toast.success(res.data.data ?? `Pump ${cmd}ed`);
        await Promise.all([loadData(true), refreshTL()]);
      } else {
        toast.error(res.message ?? "Command failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setCmdPending(null);
      setRotating(false);
      onCommand?.(cmd);
    }
  };

  // ── Auto / Manual toggle ──────────────────────────────────────────────────

  const handleModeToggle = async () => {
    if (!data) return;
    const newMode = data.auto_Manual === 1 ? 0 : 1;
    setModeBusy(true);
    try {
      const res = await ApiService.axiosApi("/instrumentmaster/update-auto_manual", {
        method: "PUT",
        data: { id: data.id, auto_Manual: newMode },
      });
      if (res.success) {
        toast.success(newMode === 1 ? "Switched to Manual" : "Switched to Auto");
         setTimeout(() => {
          loadData(true)
        }, 1000);;
      } else {
        toast.error(res.message ?? "Mode switch failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setModeBusy(false);
    }
  };

  // ── Refresh (INTG) ────────────────────────────────────────────────────────

  const handleIntg = async () => {
    if (!data) return;
    setRefreshLoading(true);
    setModeBusy(true);
    try {
      const res = await ApiService.axiosApi("/valvecommand/intg", {
        method: "POST",
        data: { instrumentId: data.id },
      });
      if (res.success) {
        toast.success("Refreshed");
        await loadData(true);
      } else {
        toast.error(res.message ?? "Refresh failed! Please try again later.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setModeBusy(false);
      setRefreshLoading(false);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────

  if (loading) return (
    <div style={styles.stateBox}>
      <i className="ti ti-loader-2" aria-hidden="true" style={{ fontSize: 20, color: PRIMARY, animation: "vdspin .9s linear infinite" }} />
      <span style={styles.stateText}>Loading pump data…</span>
      <style>{`@keyframes vdspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error || !data) return (
    <div style={{ ...styles.stateBox, border: "1px solid #fca5a5" }}>
      <i className="ti ti-alert-circle" aria-hidden="true" style={{ fontSize: 20, color: "#A32D2D" }} />
      <span style={{ ...styles.stateText, color: "#A32D2D" }}>{error ?? "No data found."}</span>
      <button onClick={() => loadData()} style={styles.retryBtn}>Retry</button>
    </div>
  );

  // ── Derived state ─────────────────────────────────────────────────────────

  const isRunning = data.status === 1;
  const isAuto    = data.auto_Manual === 0;
  const highTemp  = data.highTemperatureAlarm === 1;
  const lowBatt   = data.lowBatteryAlarm === 1;
  const hasAlarm  = data.alarmBit === 1;
  const anyAlarm  = hasAlarm || highTemp || lowBatt;
  const cmdBusy   = !!cmdPending || refreshing;

  const tlFiltered = tlEvs.filter(ev => TL_FILTERS.find(f => f.key === tlFilter)!.match(ev));
  const tlGrouped  = groupByDate(tlFiltered);
  const pmp_on = pumpon;
  const pmp_off = pumpoff;
  return (
    <>
      <Breadcrumbs mainTitle="Pump Detail" parent="User Lookup" />
      <style>{`
        @keyframes vdspin   { to { transform: rotate(360deg); } }
        @keyframes vdpulse  { 0%,100% { opacity:1 } 50% { opacity:.3 } }
        @keyframes vdrotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes spin     { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      `}</style>

      <div style={styles.page}>

        {/* ════ 50/50 split ════ */}
        <div style={styles.splitRow}>

          {/* ──────────── LEFT: detail ──────────── */}
          <div className="left-panel">

            {/* Header */}
            <div className="detail-header">
              {/* Pump icon — rotates while command is pending */}
              <div style={{
                ...styles.pumpIconBox,
                background:  isRunning ? "#e8f2ed" : "#f1f5f9",
                border:      `1px solid ${isRunning ? "#c6ddd3" : "#e2e8f0"}`,
                animation:   rotating ? "vdrotate 0.7s linear infinite" : "none",
              }}>
                <img src={isRunning ? pmp_on : pmp_off } alt="pmp_img" style={{ width:35,height:35,  fontSize: 20, color: isRunning ? PRIMARY : "#94a3b8" }} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="instrument-name">{safeStr(data.instrumentName)}</div>
                <div className="instrument-meta">
                  {safeStr(data.deviceName)} · {fmt(data.dateTime)}
                </div>
              </div>

              {/* Alarm indicator */}
              {anyAlarm && (
                <div style={styles.alarmBadge}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#E24B4A", animation: "vdpulse 1.4s infinite" }} />
                  {hasAlarm && "Alarm "}
                  {highTemp && "High Temp "}
                  {lowBatt  && "Low Batt"}
                </div>
              )}

              <button className="iconBtn" onClick={handleIntg} disabled={refreshLoading}>
                <LucideRefreshCcw
                  size={14}
                  style={{
                    animation: refreshLoading ? "spin 1s linear infinite" : "none",
                  }}
                />
              </button>
            </div>

            {/* Auto/Manual toggle + Start/Stop buttons */}
            <div className="control-row">
              <div>
                <div className="control-label">Mode</div>
                <AutoManualToggle isAuto={isAuto} busy={modeBusy} onToggle={handleModeToggle} />
              </div>

              <div style={{ flex: 1 }} />

              {/* Stop button */}
              <button
                onClick={() => !cmdBusy && handleCmd("stop")}
                disabled={cmdBusy || !isRunning}
                style={{
                  ...styles.actionBtn, ...styles.actionBtnStop,
                  opacity: (cmdBusy || !isRunning) ? 0.5 : 1,
                  cursor:  (cmdBusy || !isRunning) ? "not-allowed" : "pointer",
                }}
              >
                <i
                  className={`ti ${cmdPending === "stop" ? "ti-loader-2" : "ti-player-stop"}`}
                  aria-hidden="true"
                  style={{ fontSize: 16, animation: cmdPending === "stop" ? "vdspin .9s linear infinite" : "none" }}
                />
                Stop Pump
              </button>

              {/* Start button */}
              <button
                onClick={() => !cmdBusy && handleCmd("start")}
                disabled={cmdBusy || isRunning}
                style={{
                  ...styles.actionBtn, ...styles.actionBtnStart,
                  opacity: (cmdBusy || isRunning) ? 0.5 : 1,
                  cursor:  (cmdBusy || isRunning) ? "not-allowed" : "pointer",
                }}
              >
                <i
                  className={`ti ${cmdPending === "start" ? "ti-loader-2" : "ti-player-play"}`}
                  aria-hidden="true"
                  style={{ fontSize: 16, animation: cmdPending === "start" ? "vdspin .9s linear infinite" : "none" }}
                />
                Start Pump
              </button>
            </div>

            {/* Data grid — two columns of tables */}
            <div className="data-block">
              <Row style={{ margin: 0 }}>

                {/* Column A */}
                <Col className="col-one" style={{ padding: "0 6px 0 10px", marginRight:'5px' }}>

                  <SectionHead title="Live readings" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Status"        value={isRunning ? "Running" : "Stopped"}            valueColor={isRunning ? PRIMARY : "#E24B4A"} />
                      <DataRow label="Temperature"   value={safeNum(data.temperature, " °C")}             alarm={highTemp} />
                      <DataRow label="Battery"       value={safeNum(data.batteryVoltage, " V")}           alarm={lowBatt} />
                      <DataRow label="CT value"      value={safeNum(data.ctValue, " A")}                  valueColor={PUMP_BLUE} />
                      <DataRow label="Power"         value={bitLabel(data.p_PowerStatus, "On", "Off")}    valueColor={data.p_PowerStatus === 1 ? "#3B6D11" : undefined} />
                      <DataRow label="System health" value={bitLabel(data.p_SysHealthy, "Healthy", "Fault")} valueColor={data.p_SysHealthy === 1 ? "#3B6D11" : "#A32D2D"} />
                      <DataRow label="DI1 counter"   value={safeNum(data.dI1Counter)} last />
                      <DataRow label="Local/Remote/AutoSwitch"   value={safeStr((data.autoSwitch === 1 ? "Auto Switch" : data.p_LocalRemote === 1 ? "Local" : "Remote"))} last />
                    </tbody>
                  </table>

                  <SectionHead title="Phase status" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Phases"        value={<PhaseRow phase1={data.p_Phase1} phase2={data.p_Phase2} phase3={data.p_Phase3} />} />
                      <DataRow label="Phase fault"   value={bitLabel(data.p_PhaseFault,      "Fault", "OK")}  alarm={data.p_PhaseFault === 1} />
                      <DataRow label="Current fault" value={bitLabel(data.p_CurrentFault,    "Fault", "OK")}  alarm={data.p_CurrentFault === 1} />
                      <DataRow label="Over current"  value={bitLabel(data.p_OverCurrentBit,  "Fault", "OK")}  alarm={data.p_OverCurrentBit === 1} />
                      <DataRow label="Under current" value={bitLabel(data.p_UnderCurrentBit, "Fault", "OK")}  alarm={data.p_UnderCurrentBit === 1} last />
                    </tbody>
                  </table>

                </Col>

                {/* Column B */}
                <Col className="col-one" style={{ padding: "0 10px 0 6px", marginRight:'5px' }}>

                  <SectionHead title="Signal" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="RSSI"             value={<Sig rssi={data.rssi} />} />
                      <DataRow label="SNR"              value={safeNum(data.snr, " dB")} />
                      <DataRow label="Spreading factor" value={safeNum(data.spreadingFactor)} last />
                    </tbody>
                  </table>

                  <SectionHead title="Pump configuration" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Type"          value={safeStr(data.type)} />
                      <DataRow label="Phases"        value={data.phase !== null ? `${data.phase}-phase` : "—"} />
                      <DataRow label="Capacity"      value={safeNum(data.capacity, " HP")} />
                      <DataRow label="Over current"  value={safeNum(data.overCurrent, " A")}  valueColor="#E24B4A" />
                      <DataRow label="Under current" value={safeNum(data.underCurrent, " A")} valueColor="#854F0B" />
                      <DataRow label="Firmware"      value={safeNum(data.firmwareVersion)} />
                      <DataRow label="Packet type"   value={safeStr(data.packetType)} />

                    </tbody>
                  </table>

                  <SectionHead title="Bypass config" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Phase bypass"         value={bitLabel(data.byPassPhase,         "Bypassed", "Active")} valueColor={data.byPassPhase === 1 ? "#854F0B" : "#3B6D11"} />
                      <DataRow label="Under current bypass" value={bitLabel(data.byPassUnderCurrent,  "Bypassed", "Active")} valueColor={data.byPassUnderCurrent === 1 ? "#854F0B" : "#3B6D11"} />
                      <DataRow label="Over current bypass"  value={bitLabel(data.byPassOverCurrent,   "Bypassed", "Active")} valueColor={data.byPassOverCurrent === 1 ? "#854F0B" : "#3B6D11"} last />
                    </tbody>
                  </table>

                </Col>
              </Row>
            </div>
          </div>

          {/* ──────────── RIGHT: timeline ──────────── */}
          <div  className="rightPanel">

            <div className="tlHeader">
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <i className="ti ti-clock-hour-3" aria-hidden="true" style={{ fontSize: 13, color: "#64748b" }} />
                <span  className="panelSectionTitle" >Timeline</span>
              </div>
              <button onClick={refreshTL}  className="iconBtn">
              <LucideRefreshCcw
                  size={14}
                  style={{
                    animation: refreshLoading ? "spin 1s linear infinite" : "none",
                  }}
                />
              </button>
            </div>

            <div  className="tlFilters">
              {TL_FILTERS.map(f => {
                const cnt    = f.key === "all" ? tlEvs.length : tlEvs.filter(ev => f.match(ev)).length;
                const active = tlFilter === f.key;
                return (
                  <button key={f.key} onClick={() => {  setTlFilter(f.key);
                    tlPageRef.current = 1;
                    setTlHasMore(true);
                    loadTL(1, false); }}
                    className={`tlFilterBtn ${active ? 'tlFilterBtnActive':'' }`}
                     >
                    {f.label}
                    <span className="tlFilterCount" style={{  background: active ? PRIMARY : "#e2e8f0", color: active ? "#fff" : "#64748b" }}>
                      {cnt}
                    </span>
                  </button>
                );
              })}
            </div>

            <div ref={tlScrollRef}  className="tlBody">
              {tlLoading ? (
                <div  className="tlState">
                  <i className="ti ti-loader-2" aria-hidden="true" style={{ fontSize: 16, color: PRIMARY, animation: "vdspin .9s linear infinite" }} />
                  <span style={{ fontSize: 12, color: "#64748b" }}>Loading…</span>
                </div>
              ) : tlError ? (
                <div  className="tlState">
                  <i className="ti ti-alert-circle" aria-hidden="true" style={{ fontSize: 14, color: "#A32D2D" }} />
                  <span style={{ fontSize: 12, color: "#A32D2D" }}>{tlError}</span>
                  <button onClick={() => loadTL(1, false)}  className="retryBtn">Retry</button>
                </div>
              ) : tlFiltered.length === 0 ? (
                <div style={{ ...styles.tlState, flexDirection: "column" }}>
                  <i className="ti ti-clock-hour-3" aria-hidden="true" style={{ fontSize: 24, color: "#e2e8f0" }} />
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>No events</span>
                </div>
              ) : (
                <>
                  {tlGrouped.map(({ date, items }) => (
                    <div key={date}>
                      <DateDiv date={date} />
                      {items.map((ev, idx) => (
                        <TLCard key={ev.id} ev={ev} isLast={idx === items.length - 1} />
                      ))}
                    </div>
                  ))}
                  {/* Sentinel — IntersectionObserver triggers next API page */}
                  {tlHasMore && (
                    <div ref={tlSentinel} style={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {tlLoadingMore && (
                        <i className="ti ti-loader-2" aria-hidden="true" style={{ fontSize: 14, color: "#94a3b8", animation: "vdspin .9s linear infinite" }} />
                      )}
                    </div>
                  )}
                  {!tlHasMore && tlEvs.length > 0 && (
                    <div style={{ textAlign: "center", padding: "10px 0 2px", fontSize: 10, color: "#cbd5e1" }}>
                      — All {tlEvs.length} events loaded —
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>

        {/* ════ History table — full width ════ */}
        {historyTable && (
          <div className="historyPanel">
            <div className ='historyHeader'>

              <span className='historySectionTitle'>History</span>
            </div>
            <div style={styles.historyBody}>{historyTable}</div>
          </div>
        )}

      </div>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {

  // ── Page ──
  page: {
    display:       "flex",
    flexDirection: "column",
    gap:           10,
    paddingBottom: 24,
  },

  // ── 50/50 split ──
  splitRow: {
    display: "flex",
    gap: 10,
    alignItems: "stretch",
    height: "750px",
  },
  // ── Left panel ──
  leftPanel: {
    flex: "0 0 50%",
    width: "50%",
    background: "#fff",
    border: "1px solid #e8edf2",
    borderRadius: 10,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },

  detailHeader: {
    display:         "flex",
    alignItems:      "center",
    gap:             12,
    padding:         "14px 16px",
    borderBottom:    "1px solid #f1f5f9",
    backgroundColor: PRIMARY,
  },

  pumpIconBox: {
    width:          48,
    height:         48,
    borderRadius:   10,
    flexShrink:     0,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
  },

  instrumentName: {
    fontSize:     16,
    fontWeight:   600,
    color:        "#fff",
    whiteSpace:   "nowrap" as const,
    overflow:     "hidden",
    textOverflow: "ellipsis",
  },
  instrumentMeta: {
    fontSize:  13,
    color:     "#fff",
    marginTop: 2,
  },

  alarmBadge: {
    display:      "flex",
    alignItems:   "center",
    gap:          5,
    padding:      "4px 10px",
    borderRadius: 8,
    fontSize:     11,
    fontWeight:   500,
    background:   "#fef2f2",
    color:        "#A32D2D",
    border:       "1px solid #fca5a5",
    flexShrink:   0,
  },

  // ── Control row ──
  controlRow: {
    display:      "flex",
    alignItems:   "center",
    gap:          8,
    padding:      "10px 16px",
    borderBottom: "1px solid #f1f5f9",
    flexWrap:     "wrap" as const,
  },

  controlLabel: {
    fontSize:      10,
    color:         "#94a3b8",
    fontWeight:    600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.07em",
    marginBottom:  4,
  },

  // Auto/Manual slider
  sliderWrap: {
    display:    "inline-flex",
    alignItems: "center",
    gap:        8,
  },
  sliderSideLabel: {
    fontSize:   12,
    transition: "color .2s, font-weight .2s",
    userSelect: "none" as const,
  },
  sliderTrack: {
    position:     "relative" as const,
    width:        44,
    height:       24,
    borderRadius: 12,
    border:       "none",
    transition:   "background .25s",
    flexShrink:   0,
    padding:      0,
  },
  sliderThumb: {
    position:       "absolute" as const,
    top:            3,
    left:           3,
    width:          18,
    height:         18,
    borderRadius:   "50%",
    background:     "#fff",
    boxShadow:      "0 1px 4px rgba(0,0,0,0.25)",
    transition:     "transform .25s cubic-bezier(.4,0,.2,1)",
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
  },

  // Start / Stop buttons
  actionBtn: {
    display:      "inline-flex",
    alignItems:   "center",
    gap:          7,
    padding:      "9px 20px",
    borderRadius: 9,
    fontSize:     13,
    fontWeight:   600,
    border:       "none",
    cursor:       "pointer",
    transition:   "opacity .15s",
  },
  actionBtnStart: { background: PRIMARY, color: "#fff" },
  actionBtnStop:  { background: DANGER,  color: "#fff" },

  // ── Data block ──
  dataBlock: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },
  sectionHead: {
    fontSize:      13,
    fontWeight:    700,
    color:         "#334155",
    textTransform: "uppercase" as const,
    letterSpacing: "0.07em",
    padding:       "10px 0 5px",
    borderBottom:  "1px solid #e8edf2",
    marginBottom:  0,
  },

  dataTable: {
    width:          "100%",
    borderCollapse: "collapse" as const,
    marginBottom:   8,
  },
  tdLabel: {
    fontSize: 13,
    color:    "#64748b",
    padding:  "6px 0",
    width:    "55%",
  },
  tdValue: {
    fontSize:   12,
    fontWeight: 700,
    color:      "#1e293b",
    textAlign:  "right" as const,
    padding:    "6px 0",
  },

  // ── Right panel ──
  rightPanel: {
    flex: "0 0 calc(50% - 10px)",
  width: "calc(50% - 10px)",
  background: "#fff",
  border: "1px solid #e8edf2",
  borderRadius: 10,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  height: "100%",

  minHeight: 0, // ADD THIS
  },

  tlHeader: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "10px 14px",
    borderBottom:   "1px solid #f1f5f9",
    background:      PRIMARY,
  },

  panelSectionTitle: {
    fontSize:      16,
    fontWeight:    600,
    color:         "#FFF",
    textTransform: "uppercase" as const,
    letterSpacing: "0.07em",
  },

  iconBtn: {
    display:         "inline-flex",
    alignItems:      "center",
    justifyContent:  "center",
    width:           30,
    height:          30,
    borderRadius:    7,
    backgroundColor: "#e8f2ed",
    border:          "1px solid #e2e8f0",
    cursor:          "pointer",
  },

  tlFilters: {
    display:      "flex",
    gap:          4,
    padding:      "7px 14px",
    borderBottom: "1px solid #f1f5f9",
    flexWrap:     "wrap" as const,
  },
  tlFilterBtn: {
    display:      "inline-flex",
    alignItems:   "center",
    gap:          5,
    padding:      "3px 9px",
    borderRadius: 7,
    fontSize:     11,
    fontWeight:   500,
    cursor:       "pointer",
    background:   "transparent",
    color:        "#64748b",
    border:       "1px solid transparent",
  },
  tlFilterBtnActive: {
    background: "#e8f2ed",
    color:      PRIMARY,
    border:     "1px solid #c6ddd3",
  },
  tlFilterCount: {
    minWidth:       16,
    height:         16,
    borderRadius:   8,
    fontSize:       10,
    fontWeight:     600,
    padding:        "0 4px",
    display:        "inline-flex",
    alignItems:     "center",
    justifyContent: "center",
  },

  tlBody: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    padding:10,
    paddingBottom: 20, // ADD
  },
  tlState: {
    display:    "flex",
    alignItems: "center",
    gap:        8,
    padding:    "20px 0",
  },

  // Timeline rows
  tlRow: {
    display:    "flex",
    gap:        8,
    alignItems: "flex-start",
  },
  tlSpine: {
    display:       "flex",
    flexDirection: "column",
    alignItems:    "center",
    flexShrink:    0,
    paddingTop:    2,
  },
  tlDot: {
    width:          44,
    height:         44,
    borderRadius:   "50%",
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    zIndex:         1,
  },
  tlLine: {
    width:     1,
    flex:      1,
    minHeight: 25,
    background:"rgb(177, 178, 179)",
    marginTop: 3,
  },
  tlCard: {
    flex:         1,
    background:   "#fff",
    border:       "1px solid rgb(177, 178, 179)",
    borderRadius: 8,
  },
  tlCardHead: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "5px 9px",
  },
  tlTag: {
    display:      "inline-flex",
    alignItems:   "center",
    padding:      "1px 7px",
    borderRadius: 5,
    fontSize:     13,
    fontWeight:   500,
  },
  tlTime:  { fontSize: 13, color: "#94a3b8" },
  tlDesc:  { padding: "3px 9px 5px", fontSize: 11, color: "#475569", borderTop: "1px solid #f8fafc" },
  tlChips: { display: "flex", gap: 4, padding: "3px 9px 6px", flexWrap: "wrap" as const },
  tlChip:  { display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 6px", borderRadius: 4, fontSize: 11, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" },

  // Date divider
  dateDivider: { display: "flex", alignItems: "center", gap: 8, margin: "8px 0 6px" },
  dateDivLine: { flex: 1, height: 1, background: "#f1f5f9" },
  dateDivLabel:{ fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", whiteSpace: "nowrap" as const },

  // Load more
  loadMoreBtn: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    gap:            5,
    width:          "100%",
    marginTop:      8,
    padding:        "7px 0",
    borderRadius:   8,
    fontSize:       11,
    fontWeight:     500,
    background:     "#f8fafc",
    color:          "#64748b",
    border:         "1px solid #e2e8f0",
    cursor:         "pointer",
  },

  // ── History panel ──
  historyPanel:  { background: "#fff", border: "1px solid #e8edf2", borderRadius: 10, overflow: "hidden" },
  historyHeader: { display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" },
  historyBody:   { padding: "12px 14px" },

  // ── Generic states ──
  stateBox:  { display: "flex", alignItems: "center", gap: 10, padding: 28, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 },
  stateText: { fontSize: 13, color: "#64748b" },
  retryBtn:  { marginLeft: 8, padding: "4px 12px", borderRadius: 7, fontSize: 11, background: "#fef2f2", color: "#A32D2D", border: "1px solid #fca5a5", cursor: "pointer" },
};

export default PumpDetail;
